import { Text, View } from "@react-pdf/renderer";
import { PDFLayout, PDFTable } from "components/PDF";
import { PDFFooterProps } from "components/PDF/Footer";
import { PDFHeaderProps } from "components/PDF/Header";
import {
  RelatorioBaseData,
  RelatorioVotosFields,
} from "core/features/relatorios/typings";
import {
  VotacaoVotoIndividualItem,
  VotacaoVotosPorPerguntaItem,
} from "features/assembleia/typings";
import moment from "moment";
import React from "react";
import { usePDFRelatorioVotosColumns } from "../hooks/usePDFRelatorioVotosColumns";
import colors from "core/resources/theme/colors";

interface PDFRelatorioVotosProps {
  votosPorPergunta: VotacaoVotosPorPerguntaItem[];
  votacaoNome: string;
  selectedFields?: RelatorioVotosFields[];
  reportBaseData: Partial<RelatorioBaseData>;
}

const PDFRelatorioVotos = ({
  votosPorPergunta,
  votacaoNome,
  selectedFields = [],
  reportBaseData,
}: PDFRelatorioVotosProps): JSX.Element => {
  const { summaryTableColumns, detailTableColumns, shouldShow } =
    usePDFRelatorioVotosColumns({ selectedFields });

  const headerProps: PDFHeaderProps = {
    title: votacaoNome,
    tenant: reportBaseData.nomeTenant || "N/A",
    logo: reportBaseData.logo || "",
    subTitle: `Relatório de Votos por Pergunta - Gerado em: ${moment().format(
      "DD/MM/YYYY HH:mm"
    )}`,
  };

  const footerProps: PDFFooterProps | undefined = reportBaseData.nomeTenant
    ? {
        tenant: reportBaseData.nomeTenant,
        endereco1: reportBaseData.endereco1 || "",
        endereco2: reportBaseData.endereco2 || "",
        logo: reportBaseData.logoAdm,
      }
    : undefined;

  return (
    <PDFLayout
      headerProps={headerProps}
      footerProps={footerProps}
      assinaturas={reportBaseData.assinaturas}
      orientation="landscape"
      fontSize={9}
    >
      {votosPorPergunta.map((item) => (
        <View
          key={item.pergunta.id}
          style={{
            marginBottom: 16,
            borderBottomColor: colors.black300,
            borderBottomWidth: 1,
            borderBottomStyle: "solid",
          }}
        >
          {shouldShow("perguntaTitulo") && (
            <Text
              style={{ fontSize: 14, fontWeight: "bold", marginBottom: 4 }}
            >{`${item.pergunta.ordem + 1}. ${item.pergunta.titulo}`}</Text>
          )}

          {item.opcoes.map((opcaoItem) => (
            <View
              key={opcaoItem.opcao.id}
              style={{
                marginBottom: 8,
                borderBottomColor: colors.gray,
                borderBottomWidth: 1,
                borderBottomStyle: "dashed",
              }}
            >
              {shouldShow("opcaoTitulo") && (
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: "medium",
                    marginBottom: 4,
                  }}
                >
                  {opcaoItem.opcao.titulo}
                </Text>
              )}

              {summaryTableColumns.length > 0 && (
                <PDFTable<VotacaoVotosPorPerguntaItem["opcoes"][0]>
                  columns={summaryTableColumns}
                  data={[opcaoItem]}
                  tableHeaderVariant="rowLike"
                  wrap={false}
                />
              )}

              {detailTableColumns.length > 0 &&
                opcaoItem.votos &&
                opcaoItem.votos.length > 0 && (
                  <View style={{ marginTop: 5 }}>
                    <PDFTable<VotacaoVotoIndividualItem>
                      columns={detailTableColumns}
                      data={opcaoItem.votos}
                      tableHeaderVariant="rowLike"
                    />
                  </View>
                )}
              {detailTableColumns.length > 0 &&
                (!opcaoItem.votos || opcaoItem.votos.length === 0) && (
                  <Text>
                    Nenhum voto individual registrado para esta opção.
                  </Text>
                )}
            </View>
          ))}
          {(shouldShow("perguntaTotalVotos") ||
            shouldShow("perguntaTotalPeso")) && (
            <View>
              {shouldShow("perguntaTotalVotos") && (
                <Text style={{ fontSize: 12, fontWeight: "medium" }}>
                  Votos Totais na Pergunta: {item.totalVotos}
                </Text>
              )}
              {shouldShow("perguntaTotalPeso") && (
                <Text style={{ fontSize: 12, fontWeight: "medium" }}>
                  Peso Total na Pergunta: {item.totalPeso}
                </Text>
              )}
            </View>
          )}
        </View>
      ))}
    </PDFLayout>
  );
};

export default PDFRelatorioVotos;
